'use client';

import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import React, { type JSX, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import AboutUsBlock from '~/app/(app)/[lng]/components/home/components/about-us-block';
import CreepingLine from '~/app/(app)/[lng]/components/home/components/creeping-line';
import FaqBlock from '~/app/(app)/[lng]/components/home/components/faq-block';
import OurGoal from '~/app/(app)/[lng]/components/home/components/our-goal';
import SuggestedCourse from '~/app/(app)/[lng]/components/home/components/suggested-course';
import TitleBlock from '~/app/(app)/[lng]/components/home/components/title-block';
import { usePaginationPostsQuery } from '~/shared/apollo-queries';
import ContactsRequestForm from '~/shared/components/contacts-request-form';
import Content from '~/shared/components/content';
import PostsNavigator from '~/shared/components/posts-navigator';
import Slider from '~/shared/components/slider';
import { CONSULTATION_TYPE, LANGUAGE, LIST_ITEM_TYPE, PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getUrl } from '~/shared/helpers/string-helpers';
import { useTypedTranslation } from '~/shared/hooks';

import './home.scss';

import blogByCategoriesBg from '~/assets/blog-by-categories-bg.webp';

import aboutUs from '../../../../../assets/backgrounds/team.webp';

type PostsPropsType = {
  lng: string | null;
};

const Home = ({ lng }: PostsPropsType): JSX.Element => {
  const [activeCategoryAlias, setActiveCategoryAlias] = useState(null);
  const [isNameSortDesc, setIsNameSortDesc] = useState(null);
  const [isDateSortDesc, setIsDateSortDesc] = useState(true);
  const t = useTypedTranslation(['text', 'button', 'modal', 'image', 'field']);
  const [activeSlideIndex, setActiveSlideIndex] = useState(-1);
  const {
    data: postPaginationData,
    refetch,
    fetchMore,
  } = usePaginationPostsQuery(
    { filter: { language: lng }, perPage: 6 },
    {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    true,
    isDateSortDesc
      ? 'CREATED_AT_DESC'
      : isDateSortDesc === false
        ? 'CREATED_AT_ASC'
        : isNameSortDesc
          ? 'TITLE_DESC'
          : 'TITLE_ASC',
  );
  const items = (postPaginationData?.paginationPostQuery?.items ?? []).filter(Boolean);

  if (items.length > 1) {
    const temp = items[0];

    items[0] = items[items.length - 1];
    items[items.length - 1] = temp;
  }
  const [sliderScreen, setSliderScreen] = useState<'mobile' | 'tablet' | 'smallDesktop' | 'desktop'>('desktop');

  useEffect(() => {
    const updateSliderScreen = () => {
      const width = window.innerWidth;

      if (width <= 767) {
        setSliderScreen('mobile');
      } else if (width <= 991) {
        setSliderScreen('tablet');
      } else if (width <= 1199) {
        setSliderScreen('smallDesktop');
      } else {
        setSliderScreen('desktop');
      }
    };

    updateSliderScreen();

    window.addEventListener('resize', updateSliderScreen);

    return () => {
      window.removeEventListener('resize', updateSliderScreen);
    };
  }, []);
  const sliderRef = useRef(null);
  const sliderSlidesToShow =
    sliderScreen === 'mobile' ? 1 : sliderScreen === 'tablet' ? 2 : sliderScreen === 'smallDesktop' ? 3 : 4;

  const isMobileSlider = sliderScreen === 'mobile';
  const sliderConfig = {
    interval: 2000,
    sliderRef,
    autoPlay: false,
    centerMode: !isMobileSlider,
    centerPadding: isMobileSlider ? '0px' : '50px',
    activeSlideIndex,
    setActiveSlideIndex,
    className: 'slider__infinite home__slider',
    infiniteLoop: true,
    itemType: 'infinite-slide',
    pageToGo: '/posts/:alias',
    slidesToScroll: 1,
    slidesToShow: sliderSlidesToShow,
    getTagLink: (data) => getLocaleLink(getUrl({ link: PAGE_NAMES.POSTS_TAGS, alias: data?.alias }), lng),
    items,
  };

  useEffect(() => {
    if (sliderRef?.current && postPaginationData?.paginationPostQuery?.items?.length > 0) {
      setActiveSlideIndex(0);
      sliderRef.current?.goTo?.(0, true);
    }
  }, [sliderRef, postPaginationData?.paginationPostQuery?.items?.length, sliderScreen]);

  const router = useRouter();
  const onPostsNavigatorChange = (key: string) => {
    console.info(key);
  };
  const base = postPaginationData?.paginationPostQuery ?? {
    items: [],
    pageInfo: undefined,
  };
  const newData = { ...base, items: base.items.slice() };

  if (newData?.items?.length > 5) {
    newData?.items?.splice(4, 0, {
      description: t('text.how-to-teach'),
      _id: 'blog-banner',
      title: t('text.tell-you'),
      linkText: t('button.go-to-blog'),
      link: getLocaleLink(PAGE_NAMES.BLOG, lng),
      onLinkClick: () =>
        router.push(
          getLocaleLink(
            getUrl({
              link: PAGE_NAMES.BLOG,
            }),
            lng,
          ),
        ),
    });
  }

  return (
    <main itemScope itemType="https://schema.org/WebSite" className="home">
      <TitleBlock lng={lng} />
      {items?.length > 0 && (
        <section aria-hidden="true" className="home__main-slider">
          <Slider key={sliderScreen} {...sliderConfig} />
        </section>
      )}
      <Content>
        <AboutUsBlock
          image={aboutUs?.src}
          description={<Trans ns="text" components={{ br: <br key="br-1" /> }} i18nKey="text.about-us-text" />}
          title={t('text.home-about-us-title')}
          t={t}
        />
        <section className={classNames('container', 'home__blog-block')}>
          <div
            className="home__blog-block-bg"
            style={{
              backgroundImage: `url(${blogByCategoriesBg.src})`,
            }}
          />
          <PostsNavigator
            withLoadMore={false}
            isNameSortDesc={isNameSortDesc}
            isDateSortDesc={isDateSortDesc}
            setIsDateSortDesc={setIsDateSortDesc}
            setIsNameSortDesc={setIsNameSortDesc}
            activeCategoryAlias={activeCategoryAlias}
            setActiveCategoryAlias={setActiveCategoryAlias}
            t={t}
            getTagLink={(data) => getLocaleLink(getUrl({ link: PAGE_NAMES.POSTS_TAGS, alias: data?.alias }), lng)}
            uniqueCards={[
              {
                index: 4,
                itemType: LIST_ITEM_TYPE.BLOG_BANNER,
              },
            ]}
            data={newData}
            refetch={refetch}
            fetchMore={fetchMore}
            onPostsNavigatorChange={onPostsNavigatorChange}
          />
        </section>
        <section className="home__creeping-line">
          <CreepingLine text={t('text.read-quickly')} />
        </section>
        <section className={classNames('container')}>{lng === LANGUAGE.UA && <SuggestedCourse lng={lng} />}</section>
        <OurGoal t={t} />
        {lng === LANGUAGE.UA && <FaqBlock t={t} />}
        <section className="home__contacts-request-form">
          <ContactsRequestForm type={CONSULTATION_TYPE.ZERO_LESSON} t={t} />
        </section>{' '}
      </Content>
    </main>
  );
};

export default Home;

